/**
 * EF-SUPERADMIN-TENANT-SWITCH-V1: Tenant Context Provider
 * 
 * Global store for tenant context switching (superadmin only).
 * Allows superadmins to switch between tenants and view tenant-specific data.
 * 
 * Usage:
 * - Wrap app with <TenantContextProvider>
 * - Use useTenantContext() hook in components
 * - API client automatically includes X-Tenant-Context header
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const TenantContext = createContext(null);

// LocalStorage key
const TENANT_CONTEXT_KEY = 'tenant_context';

export function TenantContextProvider({ children }) {
  const { user } = useAuth();
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantList, setTenantList] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Check if user is superadmin
  const isSuperAdmin = user?.role === 'superadmin';
  
  // Load saved tenant context from localStorage on mount
  useEffect(() => {
    // Always try to load from localStorage first, before checking role
    const saved = localStorage.getItem(TENANT_CONTEXT_KEY);
    console.log('[TenantContext] Loading from localStorage:', saved);
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        console.log('[TenantContext] Parsed context:', parsed);
        setSelectedTenantId(parsed.tenantId);
        setSelectedTenant(parsed.tenant);
      } catch (e) {
        console.error('[TenantContext] Failed to parse localStorage:', e);
        localStorage.removeItem(TENANT_CONTEXT_KEY);
      }
    }
  }, []); // Run only once on mount
  
  // Clear context if user is not superadmin
  useEffect(() => {
    if (user && !isSuperAdmin && selectedTenantId) {
      console.log('[TenantContext] User is not superadmin, clearing context');
      localStorage.removeItem(TENANT_CONTEXT_KEY);
      setSelectedTenantId(null);
      setSelectedTenant(null);
    }
  }, [user, isSuperAdmin, selectedTenantId]);
  
  // Fetch tenant list for superadmins
  const fetchTenants = useCallback(async () => {
    if (!isSuperAdmin) return;
    
    // EF-SUPERADMIN-TENANT-SWITCH-V1: Wait for auth token to be available
    const token = localStorage.getItem('eventsflo_auth_token');
    if (!token) {
      console.log('[TenantContext] No auth token yet, skipping fetch');
      return;
    }
    
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${API_URL}/api/admin/tenants/list`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setTenantList(data);
        console.log('[TenantContext] Fetched tenants:', data.length);
      } else {
        console.warn('[TenantContext] Failed to fetch tenants:', response.status);
      }
    } catch (error) {
      console.error('[TenantContext] Failed to fetch tenants:', error);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);
  
  // Load tenants when user changes or on mount with delay
  useEffect(() => {
    if (isSuperAdmin) {
      // Small delay to ensure token is available after login
      const timer = setTimeout(() => {
        fetchTenants();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isSuperAdmin, fetchTenants, user]);
  
  // Set tenant context
  const setTenant = useCallback((tenantId, tenant = null) => {
    if (!isSuperAdmin) return;
    
    console.log('[TenantContext] Setting tenant:', tenantId, tenant?.company_name);
    
    setSelectedTenantId(tenantId);
    setSelectedTenant(tenant);
    
    if (tenantId) {
      const contextData = JSON.stringify({ tenantId, tenant });
      localStorage.setItem(TENANT_CONTEXT_KEY, contextData);
      console.log('[TenantContext] Saved to localStorage:', contextData);
    } else {
      localStorage.removeItem(TENANT_CONTEXT_KEY);
      console.log('[TenantContext] Cleared localStorage');
    }
    
    // Use setTimeout to ensure localStorage is persisted before reload
    setTimeout(() => {
      console.log('[TenantContext] Reloading page...');
      window.location.reload();
    }, 100);
  }, [isSuperAdmin]);
  
  // Clear tenant context (return to platform mode)
  const clearTenant = useCallback(() => {
    setSelectedTenantId(null);
    setSelectedTenant(null);
    localStorage.removeItem(TENANT_CONTEXT_KEY);
    
    // Reload page to refresh all data
    window.location.reload();
  }, []);
  
  // Get current context label
  const getContextLabel = useCallback(() => {
    if (!isSuperAdmin) return null;
    if (selectedTenant) return selectedTenant.company_name;
    if (selectedTenantId) return selectedTenantId;
    return 'Platform Mode';
  }, [isSuperAdmin, selectedTenant, selectedTenantId]);
  
  const value = {
    // State
    selectedTenantId,
    selectedTenant,
    tenantList,
    loading,
    
    // Computed
    isSuperAdmin,
    isViewingTenant: !!selectedTenantId,
    contextLabel: getContextLabel(),
    
    // Actions
    setTenant,
    clearTenant,
    refreshTenants: fetchTenants,
  };
  
  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenantContext() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenantContext must be used within TenantContextProvider');
  }
  return context;
}

/**
 * Get the X-Tenant-Context header value for API calls.
 * Returns null if no tenant context is set.
 */
export function getTenantHeader() {
  const saved = localStorage.getItem(TENANT_CONTEXT_KEY);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      return parsed.tenantId || null;
    } catch (e) {
      return null;
    }
  }
  return null;
}

export default TenantContext;
